"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Hotel, HotelService } from "@/services/api";

interface HotelContextType {
  hotel: Hotel | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const HotelContext = createContext<HotelContextType | undefined>(undefined);

export function HotelProvider({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get DB and Hotel ID from URL (e.g. ?db=DEMO&hotel_id=1)
  // Default to DEMO and 1 if not present
  const db = searchParams?.get("db") || "DEMO";
  const hotelId = parseInt(searchParams?.get("hotel_id") || "1");

  const fetchHotel = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await HotelService.getHotel(db, hotelId);
      setHotel(data);
    } catch (err) {
      console.error("Failed to fetch hotel:", err);
      setError("Failed to load hotel information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotel();
  }, [db, hotelId]);

  return (
    <HotelContext.Provider value={{ hotel, loading, error, refetch: fetchHotel }}>
      {children}
    </HotelContext.Provider>
  );
}

export function useHotel() {
  const context = useContext(HotelContext);
  if (context === undefined) {
    throw new Error("useHotel must be used within a HotelProvider");
  }
  return context;
}
